import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { IsActiveMatchOptions, Router, RouterModule } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';
import { CommonModule } from '@angular/common';
import { RippleModule } from 'primeng/ripple';
import { LayoutService } from '@/app/layout/service/layout.service';
import { LogoOrganismoComponent } from '@/app/shared/organismos/logo-organismo.component';

@Component({
    selector: '[app-menuitem]',
    imports: [CommonModule, RouterModule, RippleModule, TooltipModule, LogoOrganismoComponent],
    template: `
        @if (root() && isVisible()) {
            <div class="layout-menuitem-root-text">{{ item().label }}</div>
        }
        @if ((!hasRouterLink() || hasChildren()) && isVisible()) {
            <a [attr.href]="item().url" (click)="itemClick($event)" (mouseenter)="onMouseEnter()" [ngClass]="item().class" [attr.target]="item().target" tabindex="0" pRipple [pTooltip]="item().label" [tooltipDisabled]="isTooltipDisabled()">
                @if (item().organismo) {
                    <app-logo-organismo [codigo]="item().organismo" [iconoAlternativo]="item().icon" tamano="menu" class="layout-menuitem-icon" />
                } @else if (item().punto) {
                    <span class="layout-menuitem-icon menu-report-dot" aria-hidden="true"></span>
                } @else {
                    <i [ngClass]="item().icon" class="layout-menuitem-icon" aria-hidden="true"></i>
                }
                <span class="layout-menuitem-text label-small text-inherit">{{ item().label }}</span>
                @if (hasChildren()) {
                    <i class="pi pi-fw pi-angle-down layout-submenu-toggler"></i>
                }
            </a>
        }
        @if (hasRouterLink() && !hasChildren() && isVisible()) {
            <a
                (click)="itemClick($event)"
                (mouseenter)="onMouseEnter()"
                [ngClass]="item().class"
                [routerLink]="item().routerLink"
                routerLinkActive="active-route"
                [routerLinkActiveOptions]="routerLinkActiveOptions()"
                [fragment]="item().fragment"
                [queryParamsHandling]="item().queryParamsHandling"
                [preserveFragment]="item().preserveFragment"
                [skipLocationChange]="item().skipLocationChange"
                [replaceUrl]="item().replaceUrl"
                [state]="item().state"
                [queryParams]="item().queryParams"
                [attr.target]="item().target"
                tabindex="0"
                pRipple
                [pTooltip]="item().label"
                [tooltipDisabled]="isTooltipDisabled()"
            >
                @if (item().organismo) {
                    <app-logo-organismo [codigo]="item().organismo" [iconoAlternativo]="item().icon" tamano="menu" class="layout-menuitem-icon" />
                } @else if (item().punto) {
                    <span class="layout-menuitem-icon menu-report-dot" aria-hidden="true"></span>
                } @else {
                    <i [ngClass]="item().icon" class="layout-menuitem-icon" aria-hidden="true"></i>
                }
                <span class="layout-menuitem-text label-small text-inherit">{{ item().label }}</span>
                @if (hasChildren()) {
                    <i class="pi pi-fw pi-angle-down layout-submenu-toggler"></i>
                }
            </a>
        }
        @if (hasChildren() && isVisible()) {
            <ul [animate.enter]="initialized() ? 'p-submenu-enter' : null" [animate.leave]="'p-submenu-leave'" [class.layout-root-submenulist]="root()">
                @for (child of item().items; track child?.label) {
                    <li app-menuitem [item]="child" [root]="false" [parentPath]="fullPath()" [class]="child['badgeClass']"></li>
                }
            </ul>
        }
    `,
    host: {
        '[class.active-menuitem]': 'isActive()',
        '[class.layout-root-menuitem]': 'root()'
    },
    styles: [
        `
            .menu-report-dot {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                flex: 0 0 1rem;
                width: 1rem;
            }

            .menu-report-dot::before {
                content: '';
                width: 0.3rem;
                height: 0.3rem;
                border-radius: 50%;
                background: currentColor;
                opacity: 0.65;
            }

            a.active-route .menu-report-dot::before {
                background: var(--primary-color);
                opacity: 1;
            }

            /* Submenu Enter Animation */
            .p-submenu-enter {
                animation: p-animate-submenu-expand 450ms cubic-bezier(0.86, 0, 0.07, 1) forwards;
                overflow: hidden;
            }

            /* Submenu Leave Animation */
            .p-submenu-leave {
                animation: p-animate-submenu-collapse 450ms cubic-bezier(0.86, 0, 0.07, 1) forwards;
                overflow: hidden;
            }

            @keyframes p-animate-submenu-expand {
                from {
                    max-height: 0;
                }
                to {
                    max-height: 1000px;
                }
            }

            @keyframes p-animate-submenu-collapse {
                from {
                    max-height: 1000px;
                }
                to {
                    max-height: 0;
                }
            }
        `
    ]
})
export class AppMenuitem {
    layoutService = inject(LayoutService);

    router = inject(Router);

    item = input<any>(null);

    root = input<boolean>(true);

    parentPath = input<string | null>(null);

    isDisabled = computed(() => this.item()?.disabled ?? false);

    isVisible = computed(() => this.item()?.visible !== false);

    hasChildren = computed(() => this.item()?.items && this.item()?.items.length > 0);

    hasCommand = computed(() => typeof this.item()?.command === 'function');

    hasRouterLink = computed(() => !!this.item()?.routerLink);

    routerLinkActiveOptions = computed<IsActiveMatchOptions>(() => {
        if (this.item()?.routerLinkActiveOptions) {
            return this.item().routerLinkActiveOptions;
        }

        return {
            paths: 'exact',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: this.item()?.fragment ? 'exact' : 'ignored'
        };
    });

    fullPath = computed(() => {
        const itemPath = this.item()?.path;
        if (!itemPath) return this.parentPath();
        const parent = this.parentPath();
        if (parent && !itemPath.startsWith(parent)) {
            return parent + itemPath;
        }
        return itemPath;
    });

    menuHoverActive = computed(() => this.layoutService.layoutState().menuHoverActive);

    isActive = computed(() => {
        const activePath = this.layoutService.layoutState().activePath;
        if (this.item()?.path) {
            return activePath?.startsWith(this.fullPath() ?? '') ?? false;
        }
        return false;
    });

    isTooltipDisabled = computed(() => !((this.layoutService.isSlim() || this.layoutService.isSlimPlus()) && this.root() && !this.isActive()));

    initialized = signal<boolean>(false);

    constructor() {
        effect(() => {
            this.updateActivePath();
        });
    }

    updateActivePath() {
        // Don't automatically set activePath for overlay submenu modes (slim, horizontal, slimplus)
        // It should only be set through user interaction (hover, click)
        if (this.layoutService.hasOverlaySubmenu() && this.layoutService.isDesktop()) {
            return;
        }

        const item = this.item();
        const parentPath = this.parentPath();

        if (item?.routerLink && !item?.items) {
            const route = item.fragment
                ? this.router.createUrlTree(item.routerLink, {
                      fragment: item.fragment
                  })
                : item.routerLink[0];
            const isRouteActive = this.router.isActive(route, this.routerLinkActiveOptions());

            if (isRouteActive && parentPath) {
                this.layoutService.layoutState.update((val) => ({
                    ...val,
                    activePath: parentPath
                }));
            }
        }
    }

    ngAfterViewInit() {
        setTimeout(() => {
            this.initialized.set(true);
        });
    }

    itemClick(event: Event) {
        if (this.isDisabled()) {
            event.preventDefault();
            return;
        }

        if (this.hasCommand()) {
            this.item().command({ originalEvent: event, item: this.item() });
        }

        if (this.hasChildren()) {
            if (this.isActive()) {
                const deactivateHover = this.root() && this.layoutService.hasOverlaySubmenu() && this.layoutService.isDesktop();
                this.layoutService.layoutState.update((val) => ({
                    ...val,
                    activePath: this.parentPath(),
                    menuHoverActive: deactivateHover ? false : val.menuHoverActive
                }));
            } else {
                this.layoutService.layoutState.update((val) => ({
                    ...val,
                    activePath: this.fullPath(),
                    menuHoverActive: true
                }));
            }
        } else {
            this.layoutService.layoutState.update((val) => ({
                ...val,
                overlayMenuActive: false,
                mobileMenuActive: false,
                menuHoverActive: false
            }));

            if (this.layoutService.hasOverlaySubmenu() && this.layoutService.isDesktop()) {
                this.layoutService.layoutState.update((val) => ({
                    ...val,
                    activePath: null
                }));
            }
        }
    }

    onMouseEnter() {
        if (this.layoutService.isDesktop() && this.root() && this.hasChildren() && this.menuHoverActive() && !this.isActive()) {
            this.layoutService.layoutState.update((val) => ({
                ...val,
                activePath: this.fullPath(),
                menuHoverActive: true
            }));
        }
    }
}
